import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { API_BASE_URL } from '../config/api';
import { useProfile } from "../contexts/ProfileContext";
import axios from "axios";
import axiosClient from "../api/axiosClient";
import Alert from "./Alert";
import { useLanguage } from "../contexts/LanguageContext";

const ToolsPhoto = () => {
  const L = useLanguage();
  const [generating, setGenerating] = useState(false);
  const [review, setReview] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uuid, setUUID] = useState(null);
  const [functionError, setFunctionError] = useState("");
	const [functionSuccess, setFunctionSuccess] = useState("");

  const profile = useProfile();

  const handleFileChange = async (event) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      
      const formData = new FormData();
      formData.append("image", file);

      try {
        console.log("Sending post request to upload image...");
        const response = await axios.post("https://images.viralgpt.com/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("Server response:", response.data);
        setUUID(response.data.uuid);
        setPreviewUrl(URL.createObjectURL(file));
        setSelectedFile(file);
      } catch (e) {
        setFunctionError(L.toolsPhoto.uploadError(e.response?.data?.error || e.message || L.toolsPhoto.unknownError));
      }
    }
  };

  const reviewPhoto = async () => {
    if (!previewUrl) {
      setFunctionError(L.toolsPhoto.selectImage);
      return;
    }

    try {
      const formData = {};
      formData.image = uuid;
      formData.age = profile.ageRange;
      formData.gender = profile.genderTarget;
      formData.country = "USA";
      formData.niche = profile.interests;
      formData.username = profile.instagram;
      setGenerating(true);
      setReview([]);

      const response = await axiosClient.post("https://api.viralgpt.com/tools/photo", formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data && response.data.review) {
        let bulletPoints;
        
        if (Array.isArray(response.data.review)) {
          bulletPoints = response.data.review;
        } else {
          const reviewString = response.data.review.trim().slice(1, -1);
          bulletPoints = reviewString
            .split(/"],\s*"|",\s*"/)
            .map(point => point.replace(/^"|"$/g, ''));
        }
        
        bulletPoints = bulletPoints.filter(point => 
          point && point !== '[' && point !== ']' && point.trim()
        );
        
        setReview(bulletPoints);
      } else {
        throw new Error(L.toolsPhoto.invalidResponse);
      }
      setFunctionSuccess(L.toolsPhoto.success);
    } catch (error) {
      console.error("Photo review error:", error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.details ||
                          error.message || 
                          L.toolsPhoto.unexpectedError;
      setFunctionError(L.toolsPhoto.reviewError(errorMessage));
    } finally {
      setGenerating(false);
    }
  };

  const clearImage = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setUUID(null);
    document.getElementById("photoUpload").value = "";
  };

  return (
    <div className="bg-white border p-6">
      <h5>{L.toolsPhoto.heading}</h5>
      <p className="text-muted mb-0">
        {L.toolsPhoto.subtitle}
      </p>

      <Alert 
        variant="danger" 
        message={functionError} 
        show={!!functionError} 
        onClose={() => setFunctionError("")} 
      />
      
      <Alert 
        variant="success" 
        message={functionSuccess} 
        show={!!functionSuccess} 
        onClose={() => setFunctionSuccess("")} 
      />

      <div className="mb-3">
        <Form.Group controlId="photoValue" className="mb-3">
          {/* Image Preview */}
          <div className="caption-container mt-4">
            {previewUrl && (
              <img 
                className="preview-image"
                src={previewUrl} 
                alt="Preview"
              />
            )}
            {previewUrl && (
              <button 
                className="close-icon" 
                onClick={clearImage} 
                aria-label={L.toolsPhoto.closeAria}
              >
                &times;
              </button>
            )}
          </div>
          <br />
          {/* Upload Icon */}
          <div 
            style={{ marginBottom: "1rem" }} 
            className={previewUrl ? "d-none" : "d-block"}
          >
            <label htmlFor="photoUpload" className="border p-3" style={{ cursor: "pointer" }}>
              <i
                className="fas fa-image text-instagram"
                style={{ fontSize: "2rem" }}
                title={L.toolsPhoto.uploadTitle}
              />
            </label>
          </div>

          {/* Hidden File Input */}
          <Form.Control
            id="photoUpload"
            type="file"
            accept="image/*, image/heif, image/heic"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          {/* Review Button */}
          <div className="d-flex align-items-center">
            <Button 
              variant="primary"
              className={!previewUrl || generating ? "disabled bg-instagram" : "bg-instagram"}
              onClick={reviewPhoto}
              disabled={generating || !previewUrl}
            >
              {generating ? L.toolsPhoto.reviewing : L.toolsPhoto.reviewButton}
            </Button>
            <div 
              style={{fontSize: "1.5rem"}} 
              className={`ms-2 fa fa-circle-notch fa-spin text-instagram ${!generating ? "d-none" : ""}`}
            />
          </div>
        </Form.Group>

        {/* Review Results */}
        {review.length > 0 && (
          <div className="mt-4">
            {review.map((point, index) => (
              <div key={index} className="mb-4">
                <h6 className="text-instagram mb-2">
                  {index === 0 && L.toolsPhoto.sectionVisualAppeal}
                  {index === 1 && L.toolsPhoto.sectionComposition}
                  {index === 2 && L.toolsPhoto.sectionEngagementPotential}
                  {index === 3 && L.toolsPhoto.sectionOptimizationTips}
                </h6>
                <div className="d-flex align-items-start">
                  <i className="fa fa-camera text-instagram mt-1 me-3" />
                  <p className="m-0 flex-grow-1">{point}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolsPhoto;